import TollAccountType from '../types/tollAccountType';
import React from 'react';
import TollProvider from '../types/TollProvider';
import {InputValueAndError} from '../types/InputValueAndError';
import {ErrorSummaryError} from '../types/kiama';
import { LinkedAccount, ProductData, TermsConditions } from 'src/context/ApplicationContext';

/** Provide a user-friendly version of the toll account type, suitable for displaying in the UI. */
function displayableTollAccountType(tollAccountType?: TollAccountType): string {
    switch (tollAccountType) {
        case TollAccountType.Personal:
            return 'Personal';
        case TollAccountType.Business:
            return 'Business';
        case TollAccountType.Mixed:
            return 'Mixed';
        default:
            console.warn('displayableTollAccountType with no TollAccountType');
            return '';
    }
}

/** Provide a user-friendly version of the toll provider, suitable for displaying in the UI. */
function displayableTollProvider(provider?: TollProvider): string {
    switch (provider) {
        case TollProvider.EToll:
            return 'E-Toll';
        case TollProvider.Linkt:
            return 'Linkt';
        default:
            console.warn('displayableTollProvider with no TollProvider');
            return '';
    }
}

/** Used for setting of React state */
export type StateSetter<T> = React.Dispatch<React.SetStateAction<T>>;

/** A type to associate form inputs with their anchors, and error messages. */
export type FormErrorAnchor = {
    inputValue: InputValueAndError<any>,
    /** An anchor to the form input where the error occurs */
    anchor: string,
    /** A message to be displayed if there is an error */
    message: string,
};

/**
 * Given some form input values, and their potential errors,
 * collects all the inputs with errors as ErrorSummaryErrors.
 * This is to be passed into a `ErrorSummary` component.
 */
function collectErrorSummaryErrors(formInputs: FormErrorAnchor[]): ErrorSummaryError[] {
    return formInputs.reduce<ErrorSummaryError[]>((accumulator, currentValue) => {
        if (currentValue.inputValue.hasError) {
            return [...accumulator, {
                id: currentValue.anchor,
                text: currentValue.inputValue.errorMessage ?? currentValue.message,
            }];
        }
        return accumulator;
    }, []);
}

export {displayableTollAccountType, displayableTollProvider, collectErrorSummaryErrors};

/**
 * Helper to compose TRS product data to be embedded within RegistrationForm
 */
export const composeProductData = (account: LinkedAccount, termsConditions: TermsConditions): ProductData => {
    let productData: ProductData = {
        tollAccountType: account.tollAccountType,
        tollAccountDetailsPageData: {
            lpn: account.lpn,
            tollProvider: account.tollProvider as TollProvider,
            tollAccountNumber: account.tollAccountNumber,
            tollAccountEmail: account.tollAccountEmail,
        },
        agreeTerms: {
            ...termsConditions
        }
    }
    if (account.businessDetails) {
        productData = {
            ...productData,
            soleTraderDetailData: {
                abn: account.businessDetails.abn,
                businessName: account.businessDetails.abn
            }
        }
    }

    return productData;
}

/*
* Alphanum, space, dot and hyphen are allowed
 */
export const validateAlphaNumericAndSomeSymbols = (input: InputValueAndError<any>): boolean =>
    input.hasValue() &&
        /^[a-zA-Z0-9 .-]+$/.test(input.value)

/*
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/email
 */
export const validateEmailAddress = (input: InputValueAndError<string>): boolean =>
    input.hasValue() &&
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
        .test(input.value)
